import { useCallback } from 'react';

import { S3_URL_EXPIRATION_MAX_RETRIES } from '@/constants';
import { useSnackbar } from '@/hooks/useSnackbar';
import { isLikelyExpiredUrlError } from '@/utils/s3';

interface UsePdfLoadErrorHandlerParams {
  errorRetryCountRef: React.MutableRefObject<number>;
  onPdfLoadError: () => void;
  onUrlExpired?: () => void | Promise<void>;
}

export function usePdfLoadErrorHandler({
  errorRetryCountRef,
  onPdfLoadError,
  onUrlExpired
}: UsePdfLoadErrorHandlerParams) {
  const { showSnackbar } = useSnackbar();

  return useCallback(
    async (error: Error) => {
      onPdfLoadError();

      if (errorRetryCountRef.current >= S3_URL_EXPIRATION_MAX_RETRIES) {
        showSnackbar({
          message: 'Error loading document preview',
          severity: 'error'
        });
        return;
      }

      if (onUrlExpired && isLikelyExpiredUrlError(error)) {
        await onUrlExpired();
      } else {
        showSnackbar({
          message: 'Error loading document preview',
          severity: 'error'
        });
      }
    },
    [onUrlExpired, onPdfLoadError, errorRetryCountRef, showSnackbar]
  );
}
